import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Stack,
  Checkbox
} from '@mui/material';
import { Add, Delete, UploadFile, Save } from '@mui/icons-material';
import api from '../api';
import { useParams } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

interface ExamTicketSubject {
  subjectCode: string;
  subjectName: string;
  date: string;
}

const SchoolExamTicketSettings: React.FC = () => {
  const { className } = useParams<{ className: string }>();
  const decodedClassName = className ? decodeURIComponent(className) : undefined;
  const { school, updateSchoolProfile, loading, students, publishStudentDocuments } = useSchool();
  const [subjects, setSubjects] = useState<ExamTicketSubject[]>([]);
  const [instructions, setInstructions] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  const parseSaveErrorMessage = (error: any): string => {
    const responseData = error?.response?.data;
    const defaultMessage = 'Failed to save exam ticket settings. Please try again.';

    if (!responseData) {
      return error?.message || defaultMessage;
    }

    const serverMessage = responseData.message || responseData.error;
    const serverErrorDetail = responseData.error && responseData.error !== responseData.message
      ? typeof responseData.error === 'string'
        ? responseData.error
        : JSON.stringify(responseData.error)
      : '';

    if (serverMessage && serverErrorDetail) {
      return `${serverMessage}: ${serverErrorDetail}`;
    }

    if (serverMessage) {
      return String(serverMessage);
    }

    if (serverErrorDetail) {
      return serverErrorDetail;
    }

    return error?.message || defaultMessage;
  };

  const studentsInClass = useMemo(() => (
    students.filter((student) => (student.className || '').trim() === (decodedClassName || ''))
  ), [students, decodedClassName]);

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds((prev) => prev.includes(studentId)
      ? prev.filter((id) => id !== studentId)
      : [...prev, studentId]
    );
  };

  const handlePublish = async (allExcept = false, useSelection = false) => {
    setPublishMessage(null);
    setPublishError(null);
    setPublishing(true);
    try {
      const studentIds = useSelection ? selectedStudentIds : [];
      await publishStudentDocuments(decodedClassName || '', 'examTicket', studentIds, allExcept);
      setSelectedStudentIds([]);
      setPublishMessage(allExcept
        ? 'All students in this class except selected students are now published.'
        : useSelection
          ? 'Selected students are now published.'
          : 'All students in this class are now published.'
      );
    } catch (error: any) {
      setPublishError(error?.response?.data?.message || 'Failed to publish exam tickets. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    const classKey = className ? decodeURIComponent(className).trim() : '';
    const schoolData = school;
    const schoolExamTicketByClass = schoolData && Array.isArray(schoolData.examTicketByClass)
      ? schoolData.examTicketByClass
      : [];
    const classConfig = schoolExamTicketByClass.find(
      (entry: any) => String(entry.className || '').trim().toLowerCase() === classKey.toLowerCase()
    ) || null;

    if (classConfig) {
      setSubjects(
        Array.isArray(classConfig.examTicketSubjects)
          ? classConfig.examTicketSubjects.map((subject: any) => ({
              subjectCode: subject.subjectCode || '',
              subjectName: subject.subjectName || '',
              date: subject.date ? new Date(subject.date).toISOString().slice(0, 10) : ''
            }))
          : []
      );
      setInstructions(classConfig.examTicketInstructions || '');
      setSignatureUrl(classConfig.examTicketSignatureUrl || '');
    } else {
      const schoolExamTicketSubjects = schoolData && Array.isArray(schoolData.examTicketSubjects)
        ? schoolData.examTicketSubjects
        : [];
      setSubjects(
        schoolExamTicketSubjects.map((subject: any) => ({
          subjectCode: subject.subjectCode || '',
          subjectName: subject.subjectName || '',
          date: subject.date ? new Date(subject.date).toISOString().slice(0, 10) : ''
        }))
      );
      setInstructions(schoolData?.examTicketInstructions || '');
      setSignatureUrl(schoolData?.examTicketSignatureUrl || '');
    }
  }, [school, className]);

  const handleAddSubject = () => {
    setSubjects((prev) => [...prev, { subjectCode: '', subjectName: '', date: '' }]);
  };

  const handleSubjectChange = (index: number, field: keyof ExamTicketSubject, value: string) => {
    setSubjects((prev) => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
  };

  const handleRemoveSubject = (index: number) => {
    setSubjects((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;
    const file = event.target.files[0];
    setSignatureUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('school_token') || '';
      const response = await api.post('/uploads', formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadedData = response.data?.data || response.data;
      const url = uploadedData?.fileUrl || uploadedData?.url;
      if (url) {
        setSignatureUrl(url);
        setSuccess('Controller signature uploaded successfully.');
      } else {
        throw new Error('Upload response did not contain a file URL.');
      }
    } catch (uploadErr: any) {
      setError(uploadErr.response?.data?.message || uploadErr.message || 'Failed to upload signature');
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      await updateSchoolProfile({
        examTicketClassName: decodedClassName,
        examTicketSubjects: subjects.map((subject) => ({
          subjectCode: subject.subjectCode,
          subjectName: subject.subjectName,
          date: subject.date || undefined
        })),
        examTicketInstructions: instructions,
        examTicketSignatureUrl: signatureUrl || undefined
      });
      setSuccess('Exam ticket settings saved successfully.');
    } catch (saveErr: any) {
      setError(parseSaveErrorMessage(saveErr));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {decodedClassName ? `Exam Ticket Settings — ${decodedClassName}` : 'Exam Ticket Settings'}
          </Typography>
          {decodedClassName && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Configure the hall ticket details for {decodedClassName}.
            </Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || signatureUploading}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Publish Exam Tickets
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            disabled={publishing || studentsInClass.length === 0}
            onClick={() => handlePublish(false, false)}
          >
            Publish All
          </Button>
          <Button
            variant="outlined"
            disabled={publishing || selectedStudentIds.length === 0}
            onClick={() => handlePublish(false, true)}
          >
            Publish Selected Students
          </Button>
          <Button
            variant="outlined"
            disabled={publishing || studentsInClass.length === 0}
            onClick={() => handlePublish(true, true)}
          >
            Publish All Except Selected
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedStudentIds.length > 0 ? `${selectedStudentIds.length} student(s) selected.` : 'Select students below to publish either individually or use publish all actions.'}
        </Typography>
        {publishMessage && <Alert severity="success" sx={{ mb: 2 }}>{publishMessage}</Alert>}
        {publishError && <Alert severity="error" sx={{ mb: 2 }}>{publishError}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Name</TableCell>
                <TableCell>Registration</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentsInClass.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">No students found for this class.</TableCell>
                </TableRow>
              ) : (
                studentsInClass.map((student) => (
                  <TableRow key={student._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudentIds.includes(student._id)}
                        onChange={() => toggleStudentSelection(student._id)}
                        inputProps={{ 'aria-label': `Select ${student.name}` }}
                      />
                    </TableCell>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>{student.registrationId || 'N/A'}</TableCell>
                    <TableCell sx={{ color: student.examTicketPublished ? 'success.main' : 'text.secondary' }}>
                      {student.examTicketPublished ? 'Published' : 'Not published'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Exam Subjects Table
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subject Code</TableCell>
                <TableCell>Subject Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.map((subject, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      value={subject.subjectCode}
                      onChange={(event) => handleSubjectChange(index, 'subjectCode', event.target.value)}
                      placeholder="Subject code"
                      fullWidth
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={subject.subjectName}
                      onChange={(event) => handleSubjectChange(index, 'subjectName', event.target.value)}
                      placeholder="Subject name"
                      fullWidth
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={subject.date}
                      onChange={(event) => handleSubjectChange(index, 'date', event.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton edge="end" color="error" onClick={() => handleRemoveSubject(index)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {subjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                    No subjects configured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Button variant="outlined" startIcon={<Add />} onClick={handleAddSubject}>
          Add Subject
        </Button>
      </Paper>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Controller of Examination Signature
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Upload the signature image that will appear on student hall tickets.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
          <Button variant="outlined" component="label" startIcon={<UploadFile />} disabled={signatureUploading}>
            {signatureUploading ? 'Uploading…' : 'Upload Signature'}
            <input type="file" hidden accept="image/*" onChange={handleSignatureUpload} />
          </Button>
          {signatureUrl ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              Uploaded signature URL saved.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No signature uploaded yet.
            </Typography>
          )}
        </Stack>
        {signatureUrl && (
          <Box component="img" src={signatureUrl} alt="Controller Signature" sx={{ mt: 3, maxWidth: 280, maxHeight: 120, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }} />
        )}
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Instructions to Candidates
        </Typography>
        <TextField
          label="Instructions"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          multiline
          fullWidth
          minRows={4}
          placeholder="Enter exam ticket instructions for candidates."
        />
      </Paper>
    </Box>
  );
};

export default SchoolExamTicketSettings;
